import type { IMessage } from '@/modules/chat-matrix-fast/types/IChat'
import {
  attachReplySourcesToMessages,
} from '@/modules/chat-matrix-fast/services/matrixEventMapper'
import {
  findMessageIndex,
  getMessageIdentityKey,
  messagesShareNumericIdCollision,
} from '@/modules/chat-matrix-fast/utils/messageIdentity'

// Упорядочивание сообщений по времени (сначала старые)
export function compareMessagesAscending(a: IMessage, b: IMessage): number {
  const aTime = a?.created_at ? Date.parse(a.created_at) : Number.NaN
  const bTime = b?.created_at ? Date.parse(b.created_at) : Number.NaN
  if (!Number.isNaN(aTime) && !Number.isNaN(bTime)) {
    return aTime - bTime
  }

  // Фоллбек по id на случай отсутствия/некорректного времени
  const aId = a?.id ?? 0
  const bId = b?.id ?? 0

  return aId - bId
}

export function findLocalMessage(
  message: IMessage,
  localById: Map<string, IMessage>,
): IMessage | undefined {
  if (message.matrix_event_id) {
    const byEventId = localById.get(`event:${message.matrix_event_id}`)
    if (byEventId) {
      return byEventId
    }
  }

  if (message.matrix_txn_id) {
    const byTxn = localById.get(`txn:${message.matrix_txn_id}`)
    if (byTxn) {
      return byTxn
    }
  }

  const byId = localById.get(String(message.id))
  if (byId) {
    return byId
  }

  if (!message.matrix_event_id && !message.matrix_txn_id) {
    return undefined
  }

  for (const localMessage of localById.values()) {
    if (
      message.matrix_event_id
      && localMessage.matrix_event_id === message.matrix_event_id
    ) {
      return localMessage
    }
    if (
      message.matrix_txn_id
      && localMessage.matrix_txn_id === message.matrix_txn_id
    ) {
      return localMessage
    }
  }

  return undefined
}

/** Индекс локальных сообщений: сначала Matrix identity, числовой id — запасной ключ. */
export function indexLocalMessagesByIdentity(messages: IMessage[]): Map<string, IMessage> {
  const localById = new Map<string, IMessage>()
  for (const message of messages) {
    localById.set(String(message.id), message)
    if (message.matrix_event_id) {
      localById.set(`event:${message.matrix_event_id}`, message)
    }
    if (message.matrix_txn_id) {
      localById.set(`txn:${message.matrix_txn_id}`, message)
    }
  }
  return localById
}

function getMessageSenderKey(message: IMessage): string {
  return String(
    message.author_id
    || message.created_by?.id
    || message.author
    || '',
  )
}

export function isPendingMatrixEventId(eventId?: string): boolean {
  return Boolean(eventId?.startsWith('~'))
}

/** Ищет сообщение и room для реакции / редактирования */
export function resolveMatrixMessageTarget(
  messages: IMessage[],
  messageId: number | string,
  options?: {
    matrixEventIdHint?: string
    activeMessageRoomId?: string
    activeTopicRoomId?: string | null
    currentChatRoomId?: string | null
  },
): { messageIndex: number; message: IMessage; matrixEventId: string; roomId: string } | null {
  const hint = options?.matrixEventIdHint?.trim()
  const messageIndex = findMessageIndex(messages, {
    matrixEventId: hint,
    id: messageId,
  })
  if (messageIndex === -1) {
    return null
  }

  const message = messages[messageIndex]
  const matrixEventId = String(message.matrix_event_id || hint || '').trim()
  if (!matrixEventId || isPendingMatrixEventId(matrixEventId)) {
    return null
  }

  const roomId = (
    options?.activeMessageRoomId
    || options?.activeTopicRoomId
    || options?.currentChatRoomId
    || ''
  ).trim()

  if (!roomId) {
    return null
  }

  return { messageIndex, message, matrixEventId, roomId }
}

function isSameSend(a: IMessage, b: IMessage): boolean {
  // Different server event IDs always represent separate messages, even when
  // their text, attachments or transaction IDs happen to be identical.
  if (
    a.matrix_event_id && !isPendingMatrixEventId(a.matrix_event_id)
    && b.matrix_event_id && !isPendingMatrixEventId(b.matrix_event_id)
  ) {
    return false
  }

  const sender = getMessageSenderKey(a)
  return Boolean(
    sender && sender === getMessageSenderKey(b)
    && a.matrix_txn_id && a.matrix_txn_id === b.matrix_txn_id,
  )
}

function removeSendEchoes(
  byEventId: Map<string, IMessage>,
  localOnly: Map<string, IMessage>,
  message: IMessage,
): boolean {
  const incomingId = message.matrix_event_id ? String(message.matrix_event_id) : ''
  const incomingPending = isPendingMatrixEventId(message.matrix_event_id)

  for (const [eventId, existing] of byEventId) {
    if (incomingId && eventId === incomingId) {
      continue
    }
    if (!isSameSend(existing, message)) {
      continue
    }

    const existingPending = isPendingMatrixEventId(eventId)
    if (incomingPending && !existingPending) {
      return true
    }
    if (!incomingPending || existingPending) {
      byEventId.delete(eventId)
    }
  }

  for (const [localId, localMessage] of localOnly) {
    if (isSameSend(localMessage, message)) {
      localOnly.delete(localId)
    }
  }

  return false
}

/** Объединяет сообщения по matrix_event_id, не теряя уже загруженную историю */
export function mergeTimelineMessages(existing: IMessage[], incoming: IMessage[]): IMessage[] {
  const byEventId = new Map<string, IMessage>()
  const localOnly = new Map<string, IMessage>()

  const upsertMessage = (message: IMessage) => {
    if (message.matrix_event_id) {
      const eventId = String(message.matrix_event_id)
      const previous = byEventId.get(eventId)
      // A later /sync or history copy may omit the local transaction ID.
      if (!message.matrix_txn_id && previous?.matrix_txn_id) {
        message = { ...message, matrix_txn_id: previous.matrix_txn_id }
      }
      const skipPendingDuplicate = removeSendEchoes(byEventId, localOnly, message)
      if (skipPendingDuplicate) {
        return
      }
      byEventId.set(eventId, message)
      return
    }

    const localKey = getMessageIdentityKey(message)
    const previous = localOnly.get(localKey)
    if (
      previous
      && previous !== message
      && messagesShareNumericIdCollision(previous, message)
    ) {
      localOnly.set(`${localKey}:${String(message.created_at ?? '')}`, message)
      return
    }
    localOnly.set(localKey, message)
  }

  for (const message of existing) {
    upsertMessage(message)
  }

  for (const message of incoming) {
    upsertMessage(message)
  }

  for (const eventMessage of byEventId.values()) {
    for (const [localId, localMessage] of localOnly) {
      if (isSameSend(localMessage, eventMessage)) {
        localOnly.delete(localId)
      }
    }
  }

  return attachReplySourcesToMessages(
    [...byEventId.values(), ...localOnly.values()].sort(compareMessagesAscending),
  )
}

export function mergeMessageAttachmentsFromLocal(
  message: IMessage,
  localMessage?: IMessage,
): IMessage {
  if (message.is_deleted) {
    return message
  }

  if (message.attachments?.length) {
    return message
  }

  if (!localMessage?.attachments?.length) {
    return message
  }

  return {
    ...message,
    attachments: localMessage.attachments,
  }
}

export function mergeReplyMetadataFromLocal(
  message: IMessage,
  localMessage?: IMessage,
): IMessage {
  if (message.reply_to_id || message.reply_message_id || message.reply_to) {
    return message
  }

  if (!localMessage) {
    return message
  }

  if (!localMessage.reply_to_id && !localMessage.reply_message_id && !localMessage.reply_to) {
    return message
  }

  return {
    ...message,
    reply_to_id: localMessage.reply_to_id,
    reply_message_id: localMessage.reply_message_id,
    reply_to: localMessage.reply_to,
  }
}
